import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import {
  User, Buildings, UserPlus, ChartLineUp, ClockCounterClockwise,
  Robot, HardDrives, ChartBar, Pulse, Heartbeat, Notebook,
  CurrencyDollar, Stethoscope, Tag
} from "@phosphor-icons/react";
import { AdminUsersSection } from "@/components/admin/AdminUsersSection";
import { AdminModelsSection } from "@/components/admin/AdminModelsSection";
import { AdminMetricsSection } from "@/components/admin/AdminMetricsSection";
import { AdminAccountsSection, AdminUsageSection, AdminAuditSection, AdminStorageSection } from "@/components/admin/AdminSmallSections";
import { AdminFeedbackSection, AdminSystemSection, AdminTemplatesSection, AdminBillingSection, AdminClientsSection } from "@/components/admin/AdminOtherSections";
import { AdminCouponsSection } from "@/components/admin/AdminCouponsSection";
import { CodeHealthSection } from "@/components/CodeHealthSection";

const ADMIN_EMAIL = "sam@ldbsemail.com";

const SUB_TABS = [
  { id: "users", label: "Users", icon: User },
  { id: "accounts", label: "Accounts", icon: Buildings },
  { id: "clients", label: "Client Access", icon: UserPlus },
  { id: "usage", label: "Usage", icon: ChartLineUp },
  { id: "audit", label: "Audit Log", icon: ClockCounterClockwise },
  { id: "models", label: "Models", icon: Robot },
  { id: "storage", label: "Storage", icon: HardDrives },
  { id: "feedback", label: "Feedback", icon: ChartBar },
  { id: "metrics", label: "Metrics", icon: Pulse },
  { id: "system", label: "System", icon: Heartbeat },
  { id: "templates", label: "Templates", icon: Notebook },
  { id: "billing", label: "Billing", icon: CurrencyDollar },
  { id: "coupons", label: "Coupons", icon: Tag },
  { id: "health", label: "Health", icon: Stethoscope },
];

const SECTION_COMPONENTS = {
  users: AdminUsersSection,
  accounts: AdminAccountsSection,
  clients: AdminClientsSection,
  usage: AdminUsageSection,
  audit: AdminAuditSection,
  models: AdminModelsSection,
  storage: AdminStorageSection,
  feedback: AdminFeedbackSection,
  metrics: AdminMetricsSection,
  system: AdminSystemSection,
  templates: AdminTemplatesSection,
  billing: AdminBillingSection,
  coupons: AdminCouponsSection,
  health: CodeHealthSection,
};

function AdminTab() {
  const { user } = useAuth();
  const [section, setSection] = useState("users");

  if (user?.email !== ADMIN_EMAIL) return null;

  const SectionComponent = SECTION_COMPONENTS[section];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="admin-tab">
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="admin-sub-tabs">
        {SUB_TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSection(id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
              section === id
                ? "bg-[#FF4400] text-white"
                : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-raised)]"
            }`}
            data-testid={`admin-subtab-${id}`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {SectionComponent && <SectionComponent />}
    </motion.div>
  );
}

export default AdminTab;
